import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtMontant, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";

const PAD = { banque: "-", compte: "-", monnaie: "-" };
function splitBanques(banques) {
  const groups = new Map();
  banques.forEach((b) => { if (!groups.has(b.banque)) groups.set(b.banque, []); groups.get(b.banque).push(b); });
  const left = [], right = [];
  [...groups.values()].forEach((g, i) => (i % 2 === 0 ? left : right).push(...g));
  const n = Math.max(left.length, right.length, 1);
  return Array.from({ length: n }, (_, i) => [left[i] || PAD, right[i] || PAD]);
}

function Field({ label, value, mono }) {
  return (
    <div className="mb-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">{label}</span>
      <div className={`text-sm text-black ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

export default function PrintNoteDebit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/notes-debit/${id}`), api.get("/settings")])
      .then(([n, s]) => { setNote(n.data); setSettings(s.data); })
      .catch(() => navigate("/notes-debit"));
  }, [id, navigate]);

  if (!note || !settings) {
    return <div className="flex h-screen items-center justify-center bg-neutral-100"><Loader2 className="h-8 w-8 animate-spin text-neutral-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-neutral-200 py-6">
      <div className="mx-auto mb-4 flex max-w-[210mm] items-center justify-between px-4 no-print">
        <Button variant="outline" onClick={() => navigate("/notes-debit")} data-testid="back-btn"><ArrowLeft className="mr-1.5 h-4 w-4" /> Retour</Button>
        <Button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800" data-testid="do-print-btn"><Printer className="mr-1.5 h-4 w-4" /> Imprimer / PDF</Button>
      </div>

      <div className="doc-sheet print-area mx-auto max-w-[210mm] bg-white px-10 pt-6 pb-8 shadow-lg" data-testid="print-note-debit">
        {/* En-tête réservé (vide — papier à en-tête officiel) */}
        <div className="h-14 border-b border-dashed border-neutral-300" data-testid="print-header-empty" />

        <div className="mt-2 border-b-2 border-black pb-2 text-center">
          <h1 className="font-heading text-base font-bold uppercase tracking-wide">
            Fonds de Promotion du Tourisme / Note de Perception
          </h1>
          <p className="font-mono text-sm font-semibold">N° {note.numero}</p>
        </div>

        <div className="mt-2 flex justify-end">
          <div className="rounded border border-black px-3 py-0.5 text-xs">
            <span className="font-semibold">Codification du Redevable : </span>
            <span className="font-mono">{note.code}</span>
          </div>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-x-10 gap-y-0">
          <Field label="N° Note de Taxation" value={note.note_taxation} mono />
          <Field label="Mois de déclaration" value={`${note.mois}  ${note.annee}`} />
          <div className="col-span-2"><Field label="Nom et Raison" value={note.redevable} /></div>
          <Field label="Province" value={note.province} />
          <Field label="Ville" value={note.ville} />
          <Field label="Avenue / Rue" value={`${note.avenue || ""} ${note.adresse_numero ? "N° " + note.adresse_numero : ""}`.trim()} />
          <Field label="Commune" value={note.commune} />
          <Field label="Quartier" value={note.quartier} />
          <Field label="Acte(s) Générateur(s)" value={note.acte_generateur_nom} />
          <div className="col-span-2"><Field label="Libellé" value={note.note} /></div>
        </div>

        <div className="mt-2 rounded border border-neutral-300 p-2" data-testid="print-banques">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Banque de Paiement</p>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-neutral-300 text-[9px] uppercase text-neutral-500">
                <th className="py-0.5 text-left">Libellé</th><th className="py-0.5 text-left">N°Compte</th><th className="py-0.5 pr-3 text-left">Monnaie</th>
                <th className="border-l border-neutral-300 py-0.5 pl-3 text-left">Libellé</th><th className="py-0.5 text-left">N°Compte</th><th className="py-0.5 text-left">Monnaie</th>
              </tr>
            </thead>
            <tbody>
              {splitBanques(settings.banques || []).map(([l, r], i) => (
                <tr key={i} className="border-b border-neutral-100 last:border-0">
                  <td className="py-0.5 font-semibold">{l.banque}</td><td className="py-0.5 font-mono">{l.compte}</td><td className="py-0.5 pr-3 font-semibold">{l.monnaie}</td>
                  <td className="border-l border-neutral-300 py-0.5 pl-3 font-semibold">{r.banque}</td><td className="py-0.5 font-mono">{r.compte}</td><td className="py-0.5 font-semibold">{r.monnaie}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {String(note.code_taux || "") === "1" && (
          <div className="mt-3 grid grid-cols-3 gap-x-6 border-t border-neutral-300 pt-2" data-testid="print-montant-detail">
            <Field label="Pénalité" value={fmtMontant(note.penalite || 0, note.monnaie)} mono />
            <Field label="Nuitées vendues" value={fmtMontant(note.nuitees_vendues || 0, note.monnaie)} mono />
            <Field label="Repas et boissons vendus" value={fmtMontant(note.repas_boissons || 0, note.monnaie)} mono />
          </div>
        )}

        <div className="mt-3 border-y-2 border-black py-2">
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Montant</span>
            <span className="font-mono text-2xl font-bold">{fmtMontant(note.montant, note.monnaie)}</span>
          </div>
          <div className="mt-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Nous disons, en lettres :</span>
            <p className="text-sm font-medium">{note.montant_lettre}</p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-3 items-end gap-8 text-xs">
          <div>
            <p className="font-semibold">Accusé de réception</p>
            <p className="mt-1 text-neutral-500">Date : {fmtDate(note.date)}</p>
          </div>
          <div className="flex flex-col items-center">
            {note.qr && <img src={note.qr} alt="QR" className="h-20 w-20" data-testid="note-qr" />}
            <p className="mt-0.5 text-[9px] text-neutral-500">Vérification</p>
          </div>
          <div className="text-right">
            <p className="font-semibold">PREPOSÉ / {settings.sigle}</p>
            <div className="mt-6 border-t border-black pt-1 text-center text-[11px] text-neutral-500">Sceau et Signature</div>
          </div>
        </div>
      </div>
    </div>
  );
}
